import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from './SafeAnimatePresence';
import {
  FiSearch, FiX, FiMessageSquare, FiClock, FiRefreshCw,
} from 'react-icons/fi';
import type { ChatThread } from '../../features/chat/chat.types';
import { fetchRecentHistoryForAmp, CHAT_ARCHIVED_EVENT } from '../../services/historyService';
import ArchiveChatButton from './ArchiveChatButton';
import '../../styles/HistoryModal.css';

type DateFilter = 'all' | 'today' | 'week' | 'month' | 'custom';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  threads: ChatThread[];
  onSelectThread: (id: string) => void;
}

const PAGE_SIZE = 10;

const DATE_FILTERS: { id: DateFilter; label: string }[] = [
  { id: 'all', label: 'All Time' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'custom', label: 'Custom Range' },
];

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString();
}

function formatDateLabel(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - dateDay.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This Week';
  if (diffDays < 30) return 'This Month';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function isWithinDateFilter(isoString: string, filter: DateFilter): boolean {
  if (filter === 'all') return true;
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = today.getTime() - new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (filter === 'today') return diffDays === 0;
  if (filter === 'week') return diffDays < 7;
  if (filter === 'month') return diffDays < 30;
  return true;
}

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  threads,
  onSelectThread,
}) => {
  const [query, setQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Browse mode — server-paginated, 10 at a time.
  const [browseItems, setBrowseItems] = useState<ChatThread[]>([]);
  const [browseTotalCount, setBrowseTotalCount] = useState(0);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseLoadingMore, setBrowseLoadingMore] = useState(false);
  const browseOffsetRef = useRef(0);

  // Search mode — one call returns every match, no paging.
  const [searchResults, setSearchResults] = useState<ChatThread[]>([]);
  // An archived chat leaves this modal's own lists right away (the shared context
  // drops it too).
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (!id) return;
      setBrowseItems((prev) => {
        if (!prev.some((t) => t.id === id)) return prev;
        setBrowseTotalCount((n) => Math.max(0, n - 1));
        return prev.filter((t) => t.id !== id);
      });
      setSearchResults((prev) => prev.filter((t) => t.id !== id));
    };
    window.addEventListener(CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(CHAT_ARCHIVED_EVENT, onArchived);
  }, []);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestRef = useRef(0);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const lowerQuery = query.toLowerCase().trim();
  const isSearching = lowerQuery.length > 0;

  // Every list here is ordered most recently accessed first — the AMP API
  // sorts (and pages) by when the user last opened or added to each chat, and
  // the rows' updatedAt (the time shown, and the Today/Yesterday grouping)
  // carries that same timestamp.
  const loadFirstPage = useCallback(() => {
    setBrowseLoading(true);
    fetchRecentHistoryForAmp(undefined, undefined, undefined, PAGE_SIZE, 0, 'lastAccessed')
      .then(({ threads: page, totalCount }) => {
        setBrowseItems(page);
        setBrowseTotalCount(totalCount);
        browseOffsetRef.current = page.length;
      })
      .catch(() => {
        setBrowseItems([]);
        setBrowseTotalCount(0);
        browseOffsetRef.current = 0;
      })
      .finally(() => setBrowseLoading(false));
  }, []);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDateFilter('all');
      setDateFrom('');
      setDateTo('');
      setSearchResults([]);
      loadFirstPage();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, loadFirstPage]);

  // Debounced search — fires the recent-history API with searchText and
  // returns every match (backend applies no Limit/Offset in that case).
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!isSearching) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(() => {
      const requestId = ++searchRequestRef.current;
      fetchRecentHistoryForAmp(undefined, undefined, query.trim(), undefined, undefined, 'lastAccessed')
        .then(({ threads: results }) => {
          if (requestId !== searchRequestRef.current) return; // stale response
          setSearchResults(results);
        })
        .catch(() => {
          if (requestId === searchRequestRef.current) setSearchResults([]);
        })
        .finally(() => {
          if (requestId === searchRequestRef.current) setSearchLoading(false);
        });
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const loadNextPage = useCallback(() => {
    if (browseLoadingMore || browseItems.length >= browseTotalCount) return;
    setBrowseLoadingMore(true);
    fetchRecentHistoryForAmp(undefined, undefined, undefined, PAGE_SIZE, browseOffsetRef.current, 'lastAccessed')
      .then(({ threads: page, totalCount }) => {
        setBrowseItems((prev) => [...prev, ...page]);
        setBrowseTotalCount(totalCount);
        browseOffsetRef.current += page.length;
      })
      .catch(() => {})
      .finally(() => setBrowseLoadingMore(false));
  }, [browseLoadingMore, browseItems.length, browseTotalCount]);

  // Infinite scroll — only relevant while browsing (not searching).
  useEffect(() => {
    if (isSearching) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSearching, loadNextPage]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Threads created client-side this session (e.g. a chat just started) that
  // the server-paginated fetches above haven't surfaced yet — always shown
  // first, regardless of mode, so a brand-new conversation doesn't vanish
  // from History until the next full reload.
  const localOnlyThreads = useMemo(() => {
    const knownIds = new Set([...browseItems, ...searchResults].map((t) => t.id));
    return threads
      .filter((t) => !knownIds.has(t.id) && t.messages.length > 0)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [threads, browseItems, searchResults]);

  const baseList = isSearching ? searchResults : browseItems;
  const combined = useMemo(() => [...localOnlyThreads, ...baseList], [localOnlyThreads, baseList]);

  const filtered = useMemo(() => {
    return combined.filter((t) => {
      if (dateFilter === 'custom') {
        const threadDate = new Date(t.updatedAt);
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (threadDate < from) return false;
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          if (threadDate > to) return false;
        }
      } else if (!isWithinDateFilter(t.updatedAt, dateFilter)) {
        return false;
      }
      return true;
    });
  }, [combined, dateFilter, dateFrom, dateTo]);

  // Group by date label
  const grouped = useMemo(() => {
    const groups: { label: string; items: ChatThread[] }[] = [];
    let currentLabel = '';
    for (const thread of filtered) {
      const label = formatDateLabel(thread.updatedAt);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, items: [thread] });
      } else {
        groups[groups.length - 1].items.push(thread);
      }
    }
    return groups;
  }, [filtered]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const totalKnown = isSearching ? searchResults.length : localOnlyThreads.length + browseTotalCount;
  const hasMoreToLoad = !isSearching && browseItems.length < browseTotalCount;

  return (
    <SafeAnimatePresence>
      {isOpen && (
        <motion.div
          className="history-modal__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            className="history-modal glass-surface"
            initial={{ opacity: 0, y: -16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Header */}
            <div className="history-modal__header">
              <div className="history-modal__title-row">
                <FiClock size={18} className="history-modal__title-icon" />
                <h2 className="history-modal__title">Chat History</h2>
                <span className="history-modal__count">{totalKnown} conversations</span>
                <button className="history-modal__close" onClick={onClose}>
                  <FiX size={18} />
                </button>
              </div>

              <div className="history-modal__input-wrap">
                <FiSearch size={15} className="history-modal__input-icon" />
                <input
                  ref={inputRef}
                  className="history-modal__input"
                  placeholder="Search conversations..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button
                    className="history-modal__clear"
                    onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Date filter pills */}
            <div className="history-modal__filters">
              {DATE_FILTERS.map((f) => (
                <button
                  key={f.id}
                  className={`history-modal__filter ${dateFilter === f.id ? 'history-modal__filter--active' : ''}`}
                  onClick={() => {
                    setDateFilter(f.id);
                    if (f.id !== 'custom') { setDateFrom(''); setDateTo(''); }
                  }}
                >
                  {f.id !== 'all' && <FiClock size={12} />}
                  <span>{f.label}</span>
                </button>
              ))}
            </div>

            {/* Custom date range */}
            {dateFilter === 'custom' && (
              <motion.div
                className="history-modal__date-range"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <div className="history-modal__date-field">
                  <label className="history-modal__date-label">From</label>
                  <input
                    type="date"
                    className="history-modal__date-input"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    max={dateTo || undefined}
                  />
                </div>
                <span className="history-modal__date-separator">—</span>
                <div className="history-modal__date-field">
                  <label className="history-modal__date-label">To</label>
                  <input
                    type="date"
                    className="history-modal__date-input"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    min={dateFrom || undefined}
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <button
                    className="history-modal__date-clear"
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                  >
                    <FiX size={14} />
                  </button>
                )}
              </motion.div>
            )}

            {/* Results */}
            <div className="history-modal__results">
              {(browseLoading || searchLoading) && filtered.length === 0 && (
                <div className="history-modal__empty">
                  <FiRefreshCw size={20} className="history-modal__empty-icon history-modal__spin" />
                  <p>{isSearching ? `Searching for "${query}"...` : 'Loading conversations...'}</p>
                </div>
              )}

              {!browseLoading && !searchLoading && filtered.length === 0 && (
                <div className="history-modal__empty">
                  <FiMessageSquare size={32} className="history-modal__empty-icon" />
                  <p>{lowerQuery ? `No conversations matching "${query}"` : 'No conversations yet'}</p>
                </div>
              )}

              {grouped.map((group) => (
                <div key={group.label} className="history-modal__group">
                  <div className="history-modal__group-label">
                    <FiClock size={11} />
                    <span>{group.label}</span>
                  </div>
                  {group.items.map((thread, index) => (
                    <motion.div
                      key={thread.id}
                      className="history-modal__item has-archive-chat"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02, duration: 0.2 }}
                      onClick={() => { onSelectThread(thread.id); onClose(); }}
                    >
                      <FiMessageSquare size={14} className="history-modal__item-icon" />
                      <div className="history-modal__item-info">
                        <div className="history-modal__item-title">{thread.title}</div>
                        {thread.messages.length > 0 && (
                          <div className="history-modal__item-preview">
                            {thread.messages[thread.messages.length - 1].content.slice(0, 90)}
                            {thread.messages[thread.messages.length - 1].content.length > 90 ? '...' : ''}
                          </div>
                        )}
                      </div>
                      <div className="history-modal__item-meta">
                        <span className="history-modal__item-time">{formatRelativeTime(thread.updatedAt)}</span>
                        <span className="history-modal__item-msgs">{thread.messages.length} msgs</span>
                      </div>
                      <ArchiveChatButton conversationId={thread.id} />
                    </motion.div>
                  ))}
                </div>
              ))}

              {/* Infinite-scroll sentinel + "loading more" — browse mode only */}
              {!isSearching && hasMoreToLoad && (
                <div ref={sentinelRef} className="history-modal__load-more">
                  {browseLoadingMore && (
                    <FiRefreshCw size={14} className="history-modal__spin" />
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="history-modal__footer">
              <span>Press <kbd>Esc</kbd> to close</span>
              <span>{totalKnown} total conversations</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default React.memo(HistoryModal);
